/**
 * lib/employerReviews.js — Phase 1.0 reviewer-surface frontend client.
 *
 * Mirrors the LOCKED-LIVE `employerMembers.js` pattern: every write
 * delegates to the additive FastAPI shim under
 * `/api/employer/v1/reviewer/*`. Direct `supabase.rpc()` is FORBIDDEN.
 *
 * Identity contract: actor_uid is derived server-side from the JWT.
 * Slice C idempotency: every write accepts `idempotencyKey` (optional;
 * if omitted the request is non-cached).
 */
import { apiFetch } from '@/lib/api';

const BASE = '/api/employer/v1/reviewer';

const idHeaders = (key) => (key ? { 'Idempotency-Key': key } : {});

/* Admin / recruiter writes */
export function assignReviewer({ accountId, roleId, reviewerUserId, idempotencyKey }) {
  return apiFetch(`${BASE}/assignments`, {
    method: 'POST',
    headers: idHeaders(idempotencyKey),
    body: { account_id: accountId, role_id: roleId, reviewer_user_id: reviewerUserId },
  });
}

export function pauseAssignment({ accountId, assignmentId, idempotencyKey }) {
  return apiFetch(`${BASE}/assignments/${assignmentId}/pause`, {
    method: 'POST', headers: idHeaders(idempotencyKey), body: { account_id: accountId },
  });
}

export function resumeAssignment({ accountId, assignmentId, idempotencyKey }) {
  return apiFetch(`${BASE}/assignments/${assignmentId}/resume`, {
    method: 'POST', headers: idHeaders(idempotencyKey), body: { account_id: accountId },
  });
}

export function unassignReviewer({ accountId, assignmentId, idempotencyKey }) {
  return apiFetch(`${BASE}/assignments/${assignmentId}/unassign`, {
    method: 'POST', headers: idHeaders(idempotencyKey), body: { account_id: accountId },
  });
}

export function routeCandidate({
  accountId, roleId,
  candidateUserId = null,
  empCandidateId  = null,
  idempotencyKey,
}) {
  /* RC 1.62 M146 — exactly one of `candidateUserId` (NDOR-Surfaced
     revealed candidate path) or `empCandidateId` (Channel-1 Imported
     candidate path) must be supplied. The server xor-validates;
     this client mirrors the contract so calls fail fast. */
  if ((candidateUserId == null) === (empCandidateId == null)) {
    return Promise.reject(new Error(
      'routeCandidate: exactly one of candidateUserId, empCandidateId is required'
    ));
  }
  const body = { account_id: accountId, role_id: roleId };
  if (candidateUserId) body.candidate_user_id = candidateUserId;
  if (empCandidateId)  body.emp_candidate_id  = empCandidateId;
  return apiFetch(`${BASE}/reviews/route`, {
    method: 'POST',
    headers: idHeaders(idempotencyKey),
    body,
  });
}

/* Reviewer self-actions */
export function startReview({ reviewId, idempotencyKey }) {
  return apiFetch(`${BASE}/reviews/${reviewId}/start`, {
    method: 'POST', headers: idHeaders(idempotencyKey), body: {},
  });
}

export function decideReview({ reviewId, decision, rationaleExcerpt, idempotencyKey }) {
  return apiFetch(`${BASE}/reviews/${reviewId}/decide`, {
    method: 'POST',
    headers: idHeaders(idempotencyKey),
    body: { decision, rationale_excerpt: rationaleExcerpt || null },
  });
}

export function addReviewNote({ reviewId, body, idempotencyKey }) {
  return apiFetch(`${BASE}/reviews/${reviewId}/notes`, {
    method: 'POST', headers: idHeaders(idempotencyKey), body: { body },
  });
}

/* Reads */
export function fetchReviewerQueue({ status, limit = 50 } = {}) {
  const qs = new URLSearchParams();
  if (status) qs.set('status', status);
  qs.set('limit', String(limit));
  return apiFetch(`${BASE}/queue?${qs.toString()}`);
}
